@use '@/styles/variables' as *;

// ─── Keyframes ────────────────────────────────────────────────────────────────

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes contentParallax {
  to {
    transform: translateY(-60px);
    opacity: 0;
  }
}

// Wheel appears at top, scrolls down and fades, then resets
@keyframes wheelScroll {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  80% {
    transform: translateY(11px);
    opacity: 0;
  }
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

// No position: relative — lets Section's absolute videoWrapper escape the
// container and anchor against #home (the nearest positioned ancestor).
.hero {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: $section-padding-y $section-padding-x;
}

// ─── Content ──────────────────────────────────────────────────────────────────

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.75rem;
  position: relative;
  z-index: 1;
}

@supports (animation-timeline: scroll()) {
  .content {
    animation: contentParallax linear both;
    animation-timeline: #{'scroll(root)'};
    animation-range: #{'0 50vh'};
  }
}

// ─── Scroll hint ──────────────────────────────────────────────────────────────

.scrollHint {
  padding-top: 2rem;
  padding-bottom: 0.75rem;
  opacity: 1;
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
  position: relative;
  z-index: 1;

  &.hidden {
    opacity: 0;
    transform: translateY(6px);
    pointer-events: none;
  }
}

.mouse {
  width: 22px;
  height: 34px;
  border: 1.5px solid var(--muted-foreground);
  border-radius: 11px;
  opacity: 0.4;
  display: flex;
  justify-content: center;
  padding-top: 6px;
}

.wheel {
  width: 3px;
  height: 7px;
  background: var(--muted-foreground);
  border-radius: 2px;
  animation: wheelScroll 1.8s cubic-bezier(0.4, 0, 0.6, 1) 1s infinite;
}

// ─── Typography ───────────────────────────────────────────────────────────────

.tagline {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  color: var(--muted-foreground);
  text-transform: uppercase;
  margin-bottom: -0.5rem;
  animation: fadeInUp 0.6s ease-out both;
}

.heading {
  font-size: clamp(2.6rem, 6vw, 4.5rem);
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: oklch(0.1 0.02 240);
  max-width: 20ch;
  text-wrap: balance;
  animation: fadeInUp 0.6s ease-out 0.1s both;
}

:global(.dark) .heading {
  color: oklch(0.98 0.005 220);
}

.headingHighlight {
  background: linear-gradient(135deg, var(--forest), var(--water), var(--forest));
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation:
    fadeInUp 0.6s ease-out 0.1s both,
    gradientShift 10s ease-in-out 0.7s infinite;
}

.perex {
  font-size: clamp(1rem, 2vw, 1.125rem);
  color: oklch(0.36 0.02 240);
  max-width: 52ch;
  line-height: 1.75;
  animation: fadeInUp 0.6s ease-out 0.2s both;
}

:global(.dark) .perex {
  color: oklch(0.82 0.015 220);
}

// ─── CTAs ─────────────────────────────────────────────────────────────────────

.cta {
  display: flex;
  gap: 0.875rem;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 0.25rem;
  animation: fadeInUp 0.6s ease-out 0.35s both;
}

%btn-base {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.875rem;
  border-radius: var(--radius);
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition:
    opacity 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.ctaPrimary {
  @extend %btn-base;
  background-color: var(--foreground);
  color: var(--background);
  border: 1px solid transparent;

  &:hover {
    opacity: 0.85;
  }
}

.ctaSecondary {
  @extend %btn-base;
  background-color: transparent;
  color: var(--foreground);
  border: 1px solid color-mix(in oklch, var(--foreground) 35%, transparent);

  &:hover {
    border-color: var(--forest);
    color: var(--forest);
  }
}

// ─── Responsive ───────────────────────────────────────────────────────────────

@media (max-width: #{$breakpoint-xs}) {
  .cta {
    flex-direction: column;
    width: 100%;
    max-width: 20rem;
  }

  .ctaPrimary,
  .ctaSecondary {
    width: 100%;
  }
}

// ─── Reduced motion ───────────────────────────────────────────────────────────

@media (prefers-reduced-motion: reduce) {
  .tagline,
  .heading,
  .headingHighlight,
  .perex,
  .cta {
    animation: none;
    opacity: 1;
    transform: none;
  }

  .content {
    animation: none;
  }

  .scrollHint {
    transition: opacity 0.2s ease;
  }

  .wheel {
    animation: none;
    opacity: 0.6;
  }

  .headingHighlight {
    background: linear-gradient(135deg, var(--forest), var(--water));
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}
